import { memo, useCallback, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import clsx from 'clsx';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';

import { Button, PageSection, Panel, ResponsiveImage, SubHeading, Text } from 'reaxl';
import { DelayedImpressionObserver, sendClick, sendImpressions } from 'reaxl-analytics';
import { buildAndPriceClick, preorderInventoryImpressions } from 'reaxl-analytics-handlers';
import { EmailCTA } from 'reaxl-email';
import { InventoryListingBody } from 'reaxl-listing';

import { placeholderImages } from 'axl-config';

import {
    kbbVRSDataDuck,
    paymentsDuck,
    preorderDuck,
} from '@/ducks';

import {
    bapActiveModelDuck,
} from '@/ducks/bap';
import {
    vdpActiveEmailListingDuck,
    vdpActiveInteractionDuck,
    vdpResultsDuck,
} from '@/ducks/vdp';

import useBapNavigation from '@/hooks/useBapNavigation';

// Analytics

// Utilities

function PreorderSearchContainer() {
    const dispatch = useDispatch();
    const device = useDevice();
    const notXS = _get(device, 'greaterThan.xs', false);
    const orientation = ['sm', 'md'].includes(device.mediaType) ? 'landscape' : device.orientation;

    const navigateToBap = useBapNavigation();

    const {
        my_wallet: [enableMyWallet],
        preorder: [enablePreorder, {
            build_price_text: buildAndPriceButtonText,
        }],
    } = useFeatures([
        'my_wallet',
        'preorder',
    ]);

    const showListingWalletPayment = useSelector((state) => paymentsDuck.selectors.getMyWalletInteraction(state, 'paymentEmphasis'));
    const preorderListings = useSelector(preorderDuck.selectors.getPreorder);
    const vrsData = useSelector(kbbVRSDataDuck.selectors.getData);
    const vdpListing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);

    const makeCodeList = vdpListing?.makeCode;
    const modelCodeList = vdpListing?.modelCode;
    const dealerId = vdpListing?.owner?.id;
    const zip = vdpListing?.owner?.location?.address?.zip;

    const hasMakeAndModel = makeCodeList && modelCodeList;

    const showPreorderCard = enablePreorder && hasMakeAndModel;

    const preorderCtaText = enablePreorder ? buildAndPriceButtonText : 'Build & Price';

    const preorderListing = preorderListings.length > 0 && preorderListings[0];

    const hasBapData = !_isEmpty(vrsData) && dealerId;
    const setActiveModel = useCallback((keys) => {
        dispatch(bapActiveModelDuck.creators.setActiveModel(keys));
    }, [dispatch]);

    const handleRequestAQuoteClick = useCallback((listing) => async () => {
        dispatch(vdpActiveEmailListingDuck.creators.setActiveResults([listing.id]));
        dispatch(vdpActiveInteractionDuck.creators.setKeys({
            emailCTAContext: 'quote',
            emailSubject: 'New Car Custom Order Inquiry',
            ownerId: '',
            showEmailModal: true,
        }));
    }, [dispatch]);

    const handleBuildClick = useCallback((event) => {
        sendClick(buildAndPriceClick, event, {
            par: 'v_lstg',
            inventoryId: preorderListing?.id,
            ownerId: preorderListing?.owner?.id,
        });

        setActiveModel(preorderListing?.modelCode || '');

        navigateToBap({
            makeCode: preorderListing?.makeCode,
            modelCode: preorderListing?.modelCode,
            ownerId: preorderListing?.owner?.id,
            year: preorderListing?.year,
            zip: vdpListing?.owner?.location?.address?.zip,
        });
    }, [navigateToBap, preorderListing?.id, preorderListing?.makeCode, preorderListing?.modelCode, preorderListing?.owner?.id, preorderListing?.year,
        setActiveModel, vdpListing?.owner?.location?.address?.zip]);

    const handleNewCarWalletClick = useCallback((listing = {}) => async (event) => {
        event.stopPropagation();

        dispatch(paymentsDuck.creators.updateMyWallet({
            listingId: listing.id,
            showPaymentDetailsModal: true,
        }));
    }, [dispatch]);

    const getListingProps = (listing) => ({
        listing: { ...listing },
        isPhoneNumberCallable: !notXS,
        showListingPaymentRibbon: !!enableMyWallet && !!showListingWalletPayment,
        onMyWalletClick: handleNewCarWalletClick(listing),
        orientation,
        uiContext: 'preorder',
    });

    const handleInViewImpression = useCallback((listing) => {
        sendImpressions({
            data: {
                inventoryId: listing.id,
            },
            name: preorderInventoryImpressions,
        });
    }, []);

    useEffect(() => {
        if (enablePreorder && vdpListing?.makeCode && vdpListing?.modelCode) {
            const makeModelPairs = [{
                makeCode: vdpListing?.makeCode,
                modelCode: vdpListing?.modelCode,
                makeModel: `${vdpListing?.makeCode}_${vdpListing.modelCode}`,
            }];
            dispatch(kbbVRSDataDuck.creators.loadData({ makeModelPairs }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        if (showPreorderCard && !_isEmpty(vrsData)) {
            const makeModelPairs = [{
                makeCode: makeCodeList,
                modelCode: modelCodeList,
            }];
            dispatch(preorderDuck.creators.loadData({
                dealerId,
                makeModelPairs,
                vrsData,
                year: vdpListing?.year,
                zip,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, showPreorderCard, vrsData]);

    // reset Build & Price active model
    useEffect(() => {
        setActiveModel('');
    }, [setActiveModel]);

    return showPreorderCard && preorderListing && (preorderListing?.owner?.id === vdpListing?.owner?.id) && (
        <PageSection
            alignment="left"
            heading="Close but not perfect? Order the car you want!"
            uiContext="level-3"
            className="col-lg-8"
        >
            <SubHeading
                color="subdued"
                size={300}
                weight="normal"
                className="margin-bottom-4"
            >
                Work with this dealer to get the exact features and options you&apos;re looking for on your next vehicle.
            </SubHeading>
            <DelayedImpressionObserver
                onChange={() => handleInViewImpression(preorderListing)}
                key={`${preorderListing.id}-resources-delayed-impression`}
            >
                <div className="row">
                    <div className="margin-bottom-sm-5 col-lg-6 col-md-6 display-flex justify-content-center">
                        <ResponsiveImage
                            src={preorderListing.imageUrl || placeholderImages.noImageAvailable}
                            lazyload
                            alt={preorderListing.title}
                            width={preorderListing.imageUrl ? 751 : 250}
                            height={286}
                            className={clsx({ 'padding-4': !preorderListing.imageUrl })}
                        />
                    </div>
                    <div className="col-lg-6 col-md-6">
                        <Panel className="padding-vertical-4 padding-horizontal-4">
                            <InventoryListingBody
                                className="padding-0"
                                key={preorderListing.id}
                                data-cmp="preorderCardBody"
                                uiContext="preorder"
                                {...getListingProps(preorderListing)}
                            />
                            <div className="text-center">
                                <div className="margin-top-5 margin-bottom-2">
                                    <EmailCTA
                                        key="quote"
                                        buttonStyle="secondary"
                                        className={clsx('display-inline-block', { 'margin-right-2': hasBapData })}
                                        labelKey="quote"
                                        context="quote"
                                        onClick={handleRequestAQuoteClick(preorderListing)}
                                        listingId={preorderListing?.id}
                                        ownerId={preorderListing?.owner?.id}
                                        showIcon={false}
                                    />
                                    {hasBapData && (
                                        <Button
                                            bsStyle="primary"
                                            onClick={handleBuildClick}
                                        >
                                            {preorderCtaText}
                                        </Button>
                                    )}
                                </div>
                                <Text
                                    color="subdued-lightest"
                                    size={200}
                                >
                                    For illustration purposes only. May not match exact trim or color of the vehicle shown. No obligation to purchase.
                                </Text>
                            </div>
                        </Panel>
                    </div>
                </div>
            </DelayedImpressionObserver>
        </PageSection>
    );
}

export default memo(PreorderSearchContainer);
